import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import Swiper, { Navigation, Pagination, Autoplay } from 'swiper';

// Configure Swiper modules
Swiper.use([Navigation, Pagination, Autoplay]);

@Component({
  selector: 'app-banner-slider',
  templateUrl: './banner-slider.component.html',
  styleUrls: ['./banner-slider.component.scss']
})
export class BannerSliderComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() slides: Array<any> = [];
  @Input() sliderOptions: any = {
    loop: true,
    slidesPerView: 1,
    autoplay: {
      delay: 5000,
      disableOnInteraction: false
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
      type: 'bullets'
    }
  };
  @Input() bannerCardStyle: any = {};
  @Input() slot: string = '';
  
  @Output() slideClick = new EventEmitter<any>();

  @ViewChild('swiperContainer', { static: false }) swiperContainer: ElementRef | undefined;
  
  private currentVideoIndex: number = -1;
  private autoplayWasPaused: boolean = false;
  public isMuted: boolean = true; // Start muted for autoplay compatibility
  private isInitialized: boolean = false;
  private isVisible: boolean = true;
  private intersectionObserver?: IntersectionObserver;
  private routerSubscription?: Subscription;
  private visibilityChangeHandler: () => void;
  private swiper?: Swiper;

  constructor(
    private router: Router
  ) {
    this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);
  }

  ngOnInit() {
    this.setupVisibilityDetection();
    this.setupNavigationDetection();
  }

  ngAfterViewInit() {
    // Initialize Swiper with proper timing
    this.initializeSwiper();
    
    // Setup intersection observer after view init
    this.setupIntersectionObserver();
  }

  ngOnDestroy() {
    // Clean up Swiper
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    
    // Clean up visibility detection
    this.cleanupVisibilityDetection();
    
    // Clean up router subscription
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  // Setup page visibility detection
  private setupVisibilityDetection() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  // Setup navigation detection
  private setupNavigationDetection() {
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        console.log('Navigation detected, pausing videos');
        this.pauseAllVideos();
        this.pauseAutoplay();
      }
    });
  }

  // Setup intersection observer to detect component visibility
  private setupIntersectionObserver() {
    if (typeof IntersectionObserver !== 'undefined' && this.swiperContainer?.nativeElement) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            const wasVisible = this.isVisible;
            this.isVisible = entry.isIntersecting;
            
            if (wasVisible && !this.isVisible) {
              // Component became invisible
              console.log('Component not visible, pausing videos');
              this.pauseAllVideos();
              this.pauseAutoplay();
            } else if (!wasVisible && this.isVisible) {
              // Component became visible
              console.log('Component visible, resuming if appropriate');
              this.resumeIfAppropriate();
            }
          });
        },
        {
          threshold: 0.1 // Trigger when 10% of component is visible
        }
      );

      this.intersectionObserver.observe(this.swiperContainer.nativeElement);
    }
  }

  // Handle page visibility changes
  private handleVisibilityChange() {
    if (typeof document !== 'undefined') {
      if (document.hidden) {
        console.log('Page hidden, pausing videos');
        this.pauseAllVideos();
        this.pauseAutoplay();
      } else {
        console.log('Page visible, resuming if appropriate');
        this.resumeIfAppropriate();
      }
    }
  }

  // Clean up visibility detection
  private cleanupVisibilityDetection() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }
  }

  private initializeSwiper() {
    // Use timeout to ensure DOM is ready
    setTimeout(() => {
      if (this.swiperContainer?.nativeElement) {
        this.setupSwiper();
        this.isInitialized = true;
        
        // Enable loop mode after initialization if conditions are met
        this.enableLoopMode();
        
        // Handle initial slide
        this.handleSlideChange();
      } else {
        // Retry if not ready yet
        this.retryInitialization();
      }
    }, 300);
  }

  private setupSwiper() {
    if (this.swiperContainer?.nativeElement) {
      const effectiveOptions = {
        ...this.sliderOptions,
        loop: this.shouldEnableLoop,
        on: {
          slideChange: () => {
            this.handleSlideChange();
          },
          init: () => {
            console.log('Swiper initialized');
            this.isInitialized = true;
          }
        }
      };

      this.swiper = new Swiper(this.swiperContainer.nativeElement, effectiveOptions);
    }
  }

  private retryInitialization() {
    let retryCount = 0;
    const maxRetries = 10;
    
    const retry = () => {
      if (retryCount >= maxRetries) {
        console.warn('Failed to initialize Swiper after maximum retries');
        return;
      }
      
      setTimeout(() => {
        if (this.swiperContainer?.nativeElement) {
          this.setupSwiper();
          this.isInitialized = true;
          
          // Enable loop mode after initialization
          this.enableLoopMode();
          
          this.handleSlideChange();
        } else {
          retryCount++;
          retry();
        }
      }, 100 * (retryCount + 1)); // Exponential backoff
    };
    
    retry();
  }

  onSlideClick(slide: any, index?: number) {
    // Simple click handler - emit slide click event with additional data
    console.log('slide click---', slide, index);
    
    var link = slide.hyperlink;
    
    if (slide && slide.actionType == 'PACKAGE') {
      // Emit package-related slide click for parent component to handle
      this.slideClick.emit({
        type: 'package',
        slide: slide,
        index: index,
        serviceValue: slide.serviceValue,
        packageValue: slide.packageValue
      });
    } else if (link && link.trim()) {
      // Handle hyperlink
      if (link.startsWith('http://') || link.startsWith('https://')) {
        window.open(link, '_blank');
      } else {
        this.router.navigate([link]);
      }
    } else {
      // Emit generic slide click
      this.slideClick.emit({
        type: 'click',
        slide: slide,
        index: index
      });
    }
  }

  // Toggle mute state for all videos
  toggleMute() {
    this.isMuted = !this.isMuted;
    
    // Update current playing video if any
    if (this.currentVideoIndex >= 0) {
      const currentVideo = this.getVideoElementBySlideIndex(this.currentVideoIndex);
      if (currentVideo) {
        currentVideo.muted = this.isMuted;
      }
    }
    
    // Update all video elements in the DOM
    this.updateAllVideosMuteState();
  }

  // Update mute state for all video elements (including duplicates in loop mode)
  private updateAllVideosMuteState() {
    this.slides.forEach((slide, index) => {
      if (slide?.videoUrl && slide.videoUrl.trim()) {
        // Update mute state for all instances of this video (original + duplicates)
        const videoId = `slide-video-${index}`;
        const allVideoElements = document.querySelectorAll(`#${videoId}`) as NodeListOf<HTMLVideoElement>;
        
        allVideoElements.forEach(video => {
          video.muted = this.isMuted;
        });

        // Also check within all Swiper slides for videos (extra safety)
        if (this.swiper?.slides) {
          this.swiper.slides.forEach(slide => {
            const videoElement = slide.querySelector(`#${videoId}`) as HTMLVideoElement;
            if (videoElement) {
              videoElement.muted = this.isMuted;
            }
          });
        }
      }
    });
  }

  // Check if slider has any video slides
  get hasVideoSlides(): boolean {
    return this.slides.some(slide => slide?.videoUrl && slide.videoUrl.trim());
  }

  // Check if a video is currently playing
  get isVideoPlaying(): boolean {
    return this.currentVideoIndex >= 0;
  }

  // Calculate if loop mode should be enabled based on slide count
  get shouldEnableLoop(): boolean {
    if (!this.sliderOptions.loop) {
      return false; // Loop is disabled in options
    }
    
    const slidesPerView = this.sliderOptions.slidesPerView || 1;
    // For loop mode to work properly, we need at least 3 slides or 2 * slidesPerView (whichever is higher)
    const minSlidesForLoop = Math.max(3, slidesPerView * 2);
    const canLoop = this.slides.length >= minSlidesForLoop;
    
    if (!canLoop) {
      console.log(`Loop disabled: Need at least ${minSlidesForLoop} slides for slidesPerView=${slidesPerView}, but only have ${this.slides.length}`);
    }
    
    return canLoop;
  }

  // Enable loop mode after Swiper is initialized
  private enableLoopMode() {
    if (!this.isInitialized || !this.swiper) {
      return;
    }

    if (this.shouldEnableLoop) {
      try {
        // Enable loop mode programmatically for Swiper 6.x
        this.swiper.params.loop = true;
        this.swiper.update();
        console.log('Loop mode enabled after initialization');
      } catch (error) {
        console.warn('Failed to enable loop mode:', error);
      }
    }
  }

  // Handle slide change to manage video playback
  private handleSlideChange() {
    const activeIndex = this.swiper?.activeIndex || 0;
    const realIndex = this.swiper?.realIndex; // Real index without loop
    const activeSlide = this.slides[activeIndex];
    
    console.log('handleSlideChange - activeIndex:', activeIndex, 'realIndex:', realIndex, 'slides length:', this.slides.length);
    
    // Stop previous video if any
    this.stopCurrentVideo();
    
    // Use realIndex if loop is enabled, otherwise use activeIndex
    const slideIndex = this.shouldEnableLoop ? realIndex || 0 : activeIndex;
    const targetSlide = this.slides[slideIndex];
    
    console.log('Using slideIndex:', slideIndex, 'targetSlide has video:', !!(targetSlide?.videoUrl));
    
    // Check if current slide has video
    if (targetSlide?.videoUrl && targetSlide.videoUrl.trim()) {
      this.playVideoAtIndex(slideIndex);
    } else {
      // Resume autoplay if it was paused for video (for image slides or slides without video)
      this.resumeAutoplay();
    }
  }

  // Get video element by slide index from the active slide (handles duplicate slides in loop mode)
  private getVideoElementBySlideIndex(slideIndex: number): HTMLVideoElement | null {
    if (!this.swiper) {
      return null;
    }

    // Get the active slide element from Swiper to handle duplicates in loop mode
    const activeSlideElement = this.swiper.slides[this.swiper.activeIndex];
    
    if (!activeSlideElement) {
      console.warn('No active slide element found');
      return null;
    }

    // Find the video element within the active slide
    const videoElement = activeSlideElement.querySelector(`#slide-video-${slideIndex}`) as HTMLVideoElement;
    
    if (!videoElement) {
      // Fallback: try finding any video element in the active slide
      const fallbackVideo = activeSlideElement.querySelector('video') as HTMLVideoElement;
      if (fallbackVideo) {
        console.log('Using fallback video element from active slide');
        return fallbackVideo;
      }
      console.warn(`No video element found in active slide for slideIndex: ${slideIndex}`);
    }
    
    return videoElement;
  }

  // Play video at specific index
  private playVideoAtIndex(index: number) {
    const videoElement = this.getVideoElementBySlideIndex(index);
    
    if (videoElement) {
      this.currentVideoIndex = index;
      this.pauseAutoplay();
      
      // Ensure video respects current mute state
      videoElement.muted = this.isMuted;
      videoElement.currentTime = 0;
      videoElement.play().catch((error: any) => {
        console.warn('Video autoplay failed:', error);
        this.resumeAutoplay();
      });
    }
  }

  // Stop current playing video (including duplicates in loop mode)
  private stopCurrentVideo() {
    if (this.currentVideoIndex >= 0) {
      // Pause all instances of the video (original + duplicates in loop mode)
      this.pauseAllVideoInstances(this.currentVideoIndex);
    }
    this.currentVideoIndex = -1;
  }

  // Handle video ended event
  onVideoEnded(index: number) {
    if (index === this.currentVideoIndex) {
      this.currentVideoIndex = -1;
      
      // Check if autoplay is enabled
      if (this.sliderOptions.autoplay && this.autoplayWasPaused) {
        // Resume autoplay - it will handle the slide transition
        this.resumeAutoplay();
      } else {
        // No autoplay - manually move to next slide
        setTimeout(() => {
          if (this.swiper) {
            this.swiper.slideNext();
          }
        }, 300);
      }
    }
  }

  // Handle video error
  onVideoError(index: number) {
    if (index === this.currentVideoIndex) {
      console.warn('Video playback error for slide:', index);
      this.currentVideoIndex = -1;
      this.resumeAutoplay();
    }
  }

  // Handle video load start
  onVideoLoadStart(index: number) {
    // Video is starting to load
    console.log('Video loading started for slide:', index);
  }

  // Helper method to get active slide index
  get activeIndex() {
    return this.swiper?.activeIndex || 0;
  }

  // Pause all video instances for a specific slide index (handles duplicates in loop mode)
  private pauseAllVideoInstances(slideIndex: number) {
    if (!this.swiper) {
      return;
    }

    // Find all video elements with the same slide index across all slides (including duplicates)
    const videoId = `slide-video-${slideIndex}`;
    const allVideoElements = document.querySelectorAll(`#${videoId}`) as NodeListOf<HTMLVideoElement>;
    
    allVideoElements.forEach(video => {
      if (!video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });

    // Also check within all Swiper slides for videos (extra safety)
    if (this.swiper.slides) {
      this.swiper.slides.forEach(slide => {
        const videoElement = slide.querySelector(`#${videoId}`) as HTMLVideoElement;
        if (videoElement && !videoElement.paused) {
          videoElement.pause();
          videoElement.currentTime = 0;
        }
      });
    }
  }

  // Pause all videos (including duplicates in loop mode)
  private pauseAllVideos() {
    this.slides.forEach((slide, index) => {
      if (slide?.videoUrl && slide.videoUrl.trim()) {
        // Pause all instances of this video (original + duplicates)
        this.pauseAllVideoInstances(index);
      }
    });
  }

  // Resume playback if conditions are appropriate
  private resumeIfAppropriate() {
    // Only resume if component and page are visible
    if (this.isVisible && !document.hidden) {
      // Resume autoplay
      this.resumeAutoplay();
      
      // Resume current video if there was one playing
      if (this.currentVideoIndex >= 0) {
        const currentSlide = this.slides[this.currentVideoIndex];
        if (currentSlide?.videoUrl) {
          setTimeout(() => {
            this.playVideoAtIndex(this.currentVideoIndex);
          }, 100);
        }
      }
    }
  }

  // Pause autoplay - with visibility check
  private pauseAutoplay() {
    if (this.swiper?.autoplay && this.sliderOptions.autoplay) {
      this.swiper.autoplay.stop();
      this.autoplayWasPaused = true;
    }
  }

  // Resume autoplay - with visibility check
  private resumeAutoplay() {
    if (this.swiper?.autoplay && this.sliderOptions.autoplay && this.autoplayWasPaused) {
      // Only resume if component is visible and page is not hidden
      if (this.isVisible && !document.hidden) {
        this.swiper.autoplay.start();
        this.autoplayWasPaused = false;
      }
    }
  }
}
